import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import Pagination from 'react-js-pagination';
import baseUrl from '../BaseUrl';
import { readSavedFilters, usePersistFilters } from '../../hooks/filterPersistence';
import SignupSurveyFilters from './SignupSurveyFilters';
import SignupSurveyTable from './SignupSurveyTable';
import GrantAccessModal from '../free-grants/GrantAccessModal';
import createGrantApi, { grantedMessage } from '../free-grants/grantApi';
import EmptyState from '../active-users/states/EmptyState';
import ErrorState from '../active-users/states/ErrorState';
import TableSkeleton from '../active-users/states/TableSkeleton';
import '../active-users/active-users.css';
import '../free-grants/free-grants.css';
import '../survey/survey-responses.css';
import './signup-survey.css';

const PAGE_SIZE = 20;
// Opening a response is a real navigation to /signup-survey-response/:id, so Back fully remounts
// this tab. Without a snapshot the operator loses their filters and their place every time they
// read an answer (feature 045's shared hook does the storing).
const STATE_KEY = 'signupSurveyResponsesState';
const EMPTY_FILTERS = {
  search: '', support: '', understanding: '',
  keep_using_min: '', keep_using_max: '',
  from: '', to: '',
};

/**
 * Signup Survey Response — the TAB BODY (069).
 *
 * A sibling of the Survey Responses tab, not a mode of it. The two questionnaires share
 * storage but no questions: this one has no satisfaction and no recommendation, and that
 * one has no setup rating and nobody asking to be called back. Rendering both through one
 * set of columns would give whichever survey lost a table of dashes, so each source gets
 * its own tab — the same reasoning that put four sources on this screen in the first place.
 *
 * The response VIEW is shared too, through `survey/SurveyResponsePage#createResponsePage`: the
 * page's shape is identical for both questionnaires, so only the endpoint, the way back and the
 * four derived answers worth showing are named per survey.
 */
export default function SignupSurveyBody() {
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  // Seeded lazily from the snapshot: `usePersistFilters` writes on mount too, so an
  // effect-based restore would be clobbered by the defaults it had already saved.
  const [page, setPage] = useState(() => readSavedFilters(STATE_KEY)?.page || 1);
  const [surveyTitle, setSurveyTitle] = useState('15 Day Trial Feedback');
  const [filters, setFilters] = useState(() => readSavedFilters(STATE_KEY)?.filters || EMPTY_FILTERS);
  const [exportMsg, setExportMsg] = useState('');
  // Free access grant (065): the responder whose modal is open, and the last outcome.
  const [grantUser, setGrantUser] = useState(null);
  const [grantNotice, setGrantNotice] = useState('');

  const hasActiveFilters = Object.values(filters).some((v) => v !== '' && v !== null && v !== undefined);

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(
    async (config) => {
      if (expire * 1000 < new Date().getTime()) {
        const res = await axios.get(`${baseUrl}/admin/api/token`);
        config.headers.Authorization = `Bearer ${res.data.accessToken}`;
        setToken(res.data.accessToken);
        setExpire(jwtDecode(res.data.accessToken).exp);
      }
      return config;
    },
    (err) => Promise.reject(err)
  );

  useEffect(() => { refreshToken(); }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); }
    }
  };

  usePersistFilters(STATE_KEY, { filters, page });

  useEffect(() => { if (token) loadList(page, filters); /* eslint-disable-next-line */ }, [token]);

  const loadList = async (toPage, f) => {
    setLoading(true);
    setError('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/signup-survey-responses`, {
        params: { ...f, page: toPage, limit: PAGE_SIZE },
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = (res.data && res.data.data) || {};
      setItems(data.items || []);
      setTotalItem(data.totalItem || 0);
      setPage(toPage);
      if (data.survey && data.survey.title) setSurveyTitle(data.survey.title);
    } catch (e) {
      setError('Failed to load signup survey responses.');
      setItems([]);
      setTotalItem(0);
    } finally {
      setLoading(false);
    }
  };

  // The modal's server adapter — the same one /users and User 360 hand it.
  const grantApi = createGrantApi(axiosJWT, token, baseUrl);

  // The row carries the survey response, not a member: hand the modal the three identity
  // fields it reads, keyed on the responder's user_id.
  const openGrant = (row) => setGrantUser({ id: row.user_id, name: row.name, email: row.email });

  const handleGranted = (grant) => {
    setGrantUser(null);
    setGrantNotice(grantedMessage(grant));
    // The "Free trial granted" badge and its date are server-derived, so the row is wrong
    // until we refetch the page we are on.
    loadList(page, filters);
  };

  const handleApply = (f) => { setFilters(f); loadList(1, f); };
  const handleClear = () => { setFilters(EMPTY_FILTERS); loadList(1, EMPTY_FILTERS); };

  const handleExport = async () => {
    setExportMsg('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/signup-survey-responses/export`, {
        params: filters,
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'signup-survey-responses.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setExportMsg('Export failed. Please try again.');
    }
  };

  const rangeStart = totalItem === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalItem);

  return (
    <>
      <h2 className="au-title uf-tab-title sr-title">{surveyTitle}</h2>
      <p className="sr-subtitle">
        {loading ? 'Loading responses…' : `${totalItem} response${totalItem === 1 ? '' : 's'}`}
        {!loading && totalItem > 0 && <span className="sr-range"> · showing {rangeStart}–{rangeEnd}</span>}
        <span className="sr-range"> · asked after signup</span>
      </p>

      <SignupSurveyFilters values={filters} onApply={handleApply} onClear={handleClear} onExport={handleExport} />

      {exportMsg && <div className="sr-notice" role="alert">{exportMsg}</div>}

      {grantNotice && (
        <div className="fg-alert fg-alert--ok sr-grant-notice" role="status" data-testid="sr-grant-notice">
          {grantNotice}
          <button type="button" className="fg-btn" onClick={() => setGrantNotice('')}>Dismiss</button>
        </div>
      )}

      {loading ? (
        <TableSkeleton rows={8} />
      ) : error ? (
        <ErrorState message={error} onRetry={() => loadList(page, filters)} />
      ) : items.length === 0 ? (
        <EmptyState
          title="No signup survey responses found"
          message="Try adjusting the filters or widening the date range."
          hasFilters={hasActiveFilters}
          onClear={handleClear}
        />
      ) : (
        <>
          <SignupSurveyTable items={items} onGrantAccess={openGrant} />
          {totalItem > PAGE_SIZE && (
            <div className="sr-pagination">
              <Pagination
                activePage={page}
                itemsCountPerPage={PAGE_SIZE}
                totalItemsCount={totalItem}
                pageRangeDisplayed={5}
                onChange={(p) => loadList(p, filters)}
                itemClass="page-item"
                linkClass="page-link"
              />
            </div>
          )}
        </>
      )}

      {grantUser && (
        <GrantAccessModal
          user={grantUser}
          api={grantApi}
          // Names this survey, so the push thanks the member for the one they actually answered.
          source="after_signup_survey"
          onClose={() => setGrantUser(null)}
          onGranted={handleGranted}
        />
      )}
    </>
  );
}
